import React, { useCallback, useEffect, useRef, useState } from "react";
import { ClipboardList, Plus } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import moreVertIcon from "@/icons/afrimed/more_vert.svg";
import Button from "@/components/ui/Button.jsx";
import BasicDialog from "@/components/ui/BasicDialog.jsx";
import BasicDialogCritical from "@/components/ui/BasicDialogCritical.jsx";
import Select from "@/components/ui/Select.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import RouteFallback from "@/sot/primitives/RouteFallback.jsx";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import GovernedCsvImport from "../GovernedCsvImport.jsx";
import InstitutionRegistryCtas from "../InstitutionRegistryCtas.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import DatePicker from "@/sot/primitives/datePicker.jsx";
import CurrencyCodeDropdown from "@/sot/primitives/CurrencyCodeDropdown.jsx";
import FocusChip from "@/sot/primitives/FocusChip.jsx";
import {
  isValidServicePrice,
  normalizeServicePriceValue,
} from "@/sot/primitives/servicePriceCurrency.js";
import {
  getVitalRepBulletKeyEdit,
  normalizeVitalRepBulletLines,
} from "../../doctors/appointments/lib/vitalRepNote.js";
import {
  archiveInstitutionService,
  createInstitutionServiceDeliveryAssignment,
  createInstitutionService,
  loadInstitutionServiceDeliverySnapshot,
  loadInstitutionServiceSnapshot,
  updateInstitutionService,
} from "../institutionService.js";
import {
  InstitutionStatusFilterChip,
  ClinicianPickerField,
  InstitutionIcon,
  InstitutionSideSheet,
  MedicalInstitutionBackButton,
  PageShell,
  StatusPill,
} from "../InstitutionsPage.jsx";

const SERVICE_ICONS = {
  page: "/icons/medical-institution/services_filled.svg",
  empty: "/icons/medical-institution/new_services.svg",
  filter: "/icons/medical-institution/filter_list.svg",
  listStatusActive: "/icons/medical-institution/active_list_status.svg",
  statusActive: "/icons/medical-institution/active_status.svg",
  statusInactive: "/icons/medical-institution/inactive_status.svg",
  statusPending: "/icons/medical-institution/Pending.svg",
  dateRange: "/icons/medical-institution/date_range_Fill.svg",
  expandMore: "/icons/medical-institution/expand-more - 18 x 18.svg",
  add: "/icons/clinicians/add.svg",
  link: "/icons/clinicians/link.svg",
  newTab: "/icons/clinicians/new_tab.svg",
  delete: "/icons/clinicians/delete.svg",
};

const SERVICE_STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

function servicePath(institutionId, serviceId = "") {
  const encodedInstitution = encodeURIComponent(String(institutionId || ""));
  const suffix = serviceId ? `/${encodeURIComponent(String(serviceId))}` : "";
  return `/institutions/services${suffix}?institution=${encodedInstitution}`;
}

function serviceStatusLabel(t, value) {
  return t(`institutionServices.status.${value}`, value === "inactive" ? "Inactive" : value === "all" ? "All" : "Active");
}

function serviceStatusValueIcon(value) {
  return value === "active" ? SERVICE_ICONS.statusActive : SERVICE_ICONS.statusInactive;
}

function serviceListStatusIcon(value) {
  return value === "active" ? SERVICE_ICONS.listStatusActive : SERVICE_ICONS.statusInactive;
}

function deliveryStateLabel(t, value) {
  const labels = {
    actively_deliverable: "Actively deliverable",
    catalogue_unassigned: "No clinicians assigned",
    delivery_unavailable: "Delivery unavailable",
    inactive_service: "Service inactive",
    archived_service: "Service archived",
    inactive_assignment: "Assignment inactive",
    inactive_clinician_relationship: "Clinician relationship inactive",
    facility_unavailable: "Facility unavailable",
    facility_clinician_relationship_inactive: "Clinician facility relationship inactive",
    assignment_expired: "Assignment expired",
    assignment_not_yet_effective: "Not yet effective",
    archived_assignment: "Assignment archived",
  };
  return t(`institutionServices.delivery.states.${value}`, labels[value] || "Delivery unavailable");
}

function DeliveryAvailabilityState({ error, onRetry, c }) {
  const unavailable = error?.kind === "unavailable";
  const title = unavailable
    ? c("delivery.unavailableTitle", "Delivery assignments are not available yet")
    : c("delivery.loadError", "Clinician delivery assignments could not be loaded.");
  const description = unavailable
    ? c("delivery.unavailableBody", "The service delivery contract has not been activated in this environment.")
    : c("delivery.loadErrorBody", "Try again to load clinician delivery assignments.");

  return (
    <div className="institution-service-delivery-availability" role="alert">
      <p className="text-title-medium text-on-surface">{title}</p>
      <p className="text-body-medium text-on-surface-variant">{description}</p>
      <Button type="button" variant="tonal" size="default" onClick={onRetry}>
        {c("retry", "Try again")}
      </Button>
    </div>
  );
}

function deliveryDraft() {
  return { facilityId: "", clinicianId: "", status: "active", effectiveFrom: new Date().toISOString().slice(0, 10), effectiveTo: "" };
}

function deliveryDateFromValue(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value || "")) ? new Date(`${value}T12:00:00`) : null;
}

function deliveryDateToValue(value) {
  if (value === null || value === undefined || value === "") return "";
  const date = value instanceof Date ? value : new Date(value);
  return !Number.isNaN(date.getTime()) ? date.toISOString().slice(0, 10) : "";
}

function formatDeliveryDate(value) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : "";
}

function deliverySnapshotFailure(error) {
  const code = String(error?.code || "");
  if (code === "PGRST202") return { kind: "unavailable", operation: "delivery_snapshot", code, original: error };
  return { kind: "error", operation: "delivery_snapshot", code, original: error };
}

function ServiceListPrice({ value, currency = "BWP", t }) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return <span className="text-body-medium text-on-surface-variant">{t("institutionServices.priceUnavailable", "Setup pending")}</span>;
  return <><span className="text-body-medium text-on-surface">{amount.toLocaleString()}</span>{" "}<span className="text-body-medium text-on-surface-variant">{currency || "BWP"}</span></>;
}

function ServiceListField({ className, label, title, children }) {
  return (
    <span className={`institution-service-cell ${className}`} data-label={label} title={title}>
      <span className="institution-service-mobile-value">{children}</span>
    </span>
  );
}

function serviceFormFromRecord(record) {
  return {
    name: String(record?.name || ""),
    specialtyId: String(record?.specialty?.id || ""),
    price: record?.price === null || record?.price === undefined ? "" : String(record.price),
    currency: String(record?.currency || "BWP"),
    status: record?.status === "inactive" ? "inactive" : "active",
    notes: String(record?.notes || ""),
    facilityIds: (Array.isArray(record?.facilities) ? record.facilities : []).map((facility) => facility?.id).filter(Boolean),
  };
}

function ServiceListRow({ service, onOpen, t }) {
  const notes = String(service?.notes || "").trim() || t("institutionServices.notSupplied", "Not supplied");
  const notesPreview = notes.replace(/\s+/g, " ");
  const facilities = Array.isArray(service?.facilities) ? service.facilities : [];
  const facilityNames = facilities.map((facility) => facility.name).filter(Boolean).join(", ");
  const isActive = service?.status === "active";

  return (
    <button type="button" className="institution-service-row" onClick={() => onOpen(service.id)} aria-label={`${t("common.actions.open", "Open")} ${service.name}`}>
      <ServiceListField className="institution-service-name text-body-medium text-on-surface" label={t("institutionServices.mobileFields.service", "Service")}>{service.name}</ServiceListField>
      <ServiceListField className="institution-service-specialty text-body-medium text-on-surface" label={t("institutionServices.mobileFields.specialty", "Specialty")}>{service?.specialty?.name || t("institutionServices.notSupplied", "Not supplied")}</ServiceListField>
      <ServiceListField className="institution-service-status" label={t("institutionServices.mobileFields.status", "Status")}><StatusPill tone={isActive ? "success" : "neutral"}><img className="institution-service-status-badge-icon" src={serviceListStatusIcon(service.status)} alt="" /><span className="text-label-medium">{serviceStatusLabel(t, service.status)}</span></StatusPill></ServiceListField>
      <span className="institution-service-cell institution-service-notes text-body-medium text-on-surface" title={notes}>{notesPreview}</span>
      <ServiceListField className="institution-service-price" label={t("institutionServices.mobileFields.price", "Price")}><ServiceListPrice value={service.price} currency={service.currency} t={t} /></ServiceListField>
      {facilityNames ? <ServiceListField className="institution-service-facilities text-body-medium text-on-surface-variant" label={t("institutionServices.mobileFields.facility", "Medical Institution")}>{facilityNames}</ServiceListField> : null}
    </button>
  );
}

function ServicesEmptyState({ filtered = false, t }) {
  return (
    <div className="institution-services-empty flex-1 grid place-items-center text-center px-2" role="status">
      <div>
        <img src={SERVICE_ICONS.empty} alt="" className="h-[76px] w-[76px] mx-auto" draggable="false" />
        <h2 className="mt-4 text-title-large text-on-surface">{filtered ? t("institutionServices.noResultsTitle", "No services match this filter") : t("institutionServices.emptyTitle", "No services yet")}</h2>
        <p className="mt-2 text-body-large text-on-surface-variant max-w-[420px] mx-auto">{filtered ? t("institutionServices.noResultsBody", "Try a different filter.") : t("institutionServices.emptyBody", "Add the services your Medical Institution provides to patients.")}</p>
      </div>
    </div>
  );
}

function ServiceFilterFields({ draft, onChange, specialties, facilities, t }) {
  return (
    <div className="institution-service-filter-fields">
      <Select
        label={t("institutionServices.fields.specialty", "Specialty")}
        value={draft.specialty}
        options={[{ value: "", label: t("institutionServices.filters.allSpecialties", "All specialties") }, ...specialties.map((item) => ({ value: item.id, label: item.name }))]}
        onValueChange={(value) => onChange((current) => ({ ...current, specialty: value }))}
        tone="surface"
        searchable
        placeholder={t("institutionServices.selectOption", "Select an option")}
        searchPlaceholder={t("institutionServices.searchOptions", "Search options…")}
      />
      <Select
        label={t("institutionServices.fields.facility", "Medical Institution")}
        value={draft.facility}
        options={[{ value: "", label: t("institutionServices.filters.allFacilities", "All Medical Institutions") }, ...facilities.map((item) => ({ value: item.id, label: item.name }))]}
        onValueChange={(value) => onChange((current) => ({ ...current, facility: value }))}
        tone="surface"
        searchable
        placeholder={t("institutionServices.selectOption", "Select an option")}
        searchPlaceholder={t("institutionServices.searchOptions", "Search options…")}
      />
    </div>
  );
}

export function ServicesPage({ workspace }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const institutionId = workspace?.activeInstitution?.id || "";
  const canManage = Boolean(workspace?.canManageServices);
  const [status, setStatus] = useState("all");
  const [filterOpen, setFilterOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({ specialty: "", facility: "" });
  const [filterDraft, setFilterDraft] = useState({ specialty: "", facility: "" });
  const [mobileFilterSurface, setMobileFilterSurface] = useState(() => (typeof window !== "undefined" && window.matchMedia?.("(max-width: 767px)")?.matches) ?? false);
  const filterSurfaceRef = useRef(null);
  const filterTriggerRef = useRef(null);
  const [notice, setNotice] = useState(location.state?.serviceArchived ? t("institutionServices.archivedSuccess", "Service archived") : "");
  const [csvOpen, setCsvOpen] = useState(false);
  const services = Array.isArray(workspace?.services) ? workspace.services : [];
  const facilities = Array.isArray(workspace?.serviceFacilities) ? workspace.serviceFacilities : [];
  const specialties = Array.isArray(workspace?.serviceSpecialties) ? workspace.serviceSpecialties : [];
  const rows = services.filter((service) => (
    (status === "all" || service?.status === status)
    && (!advancedFilters.specialty || service?.specialty?.id === advancedFilters.specialty)
    && (!advancedFilters.facility || service?.facilities?.some((facility) => facility.id === advancedFilters.facility))
  ));
  const statusOptions = SERVICE_STATUS_OPTIONS.map((option) => ({ ...option, label: serviceStatusLabel(t, option.value) }));
  const filtered = Boolean(status !== "all" || advancedFilters.specialty || advancedFilters.facility);
  const closeFilters = useCallback(({ restoreFocus = true } = {}) => {
    setFilterOpen(false);
    setFilterDraft(advancedFilters);
    if (restoreFocus) window.requestAnimationFrame(() => filterTriggerRef.current?.focus?.());
  }, [advancedFilters]);
  const openFilters = () => {
    setFilterDraft(advancedFilters);
    setFilterOpen(true);
  };
  const clearFilterDraft = () => setFilterDraft({ specialty: "", facility: "" });
  const applyFilters = () => {
    setAdvancedFilters(filterDraft);
    setFilterOpen(false);
    window.requestAnimationFrame(() => filterTriggerRef.current?.focus?.());
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia?.("(max-width: 767px)");
    if (!mediaQuery) return undefined;
    const sync = () => setMobileFilterSurface(mediaQuery.matches);
    sync();
    mediaQuery.addEventListener?.("change", sync);
    return () => mediaQuery.removeEventListener?.("change", sync);
  }, []);

  useEffect(() => {
    if (!filterOpen || mobileFilterSurface) return undefined;
    const onClick = (event) => {
      if (!filterSurfaceRef.current?.contains(event.target)) closeFilters();
    };
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      closeFilters();
    };
    document.addEventListener("click", onClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [closeFilters, filterOpen, mobileFilterSurface]);

  return (
    <PageShell workspace={workspace} title={t("institutionServices.title", "Services")} icon={ClipboardList} iconSrc={SERVICE_ICONS.page} pageTopBarTitle={t("institutionServices.title", "Services")} contentClassName="afrimed-rail afrimed-rail--comfortable pb-24 institution-services-content">
      <section className="institutions-clinicians-page institution-services-page" aria-label={t("institutionServices.catalogue", "Institution service catalogue")}>
        <div className="institutions-clinicians-tools">
          <div className="institutions-list-filter-group" aria-label={t("institutionServices.filters.group", "Service list filters")}>
            <InstitutionStatusFilterChip value={status} options={statusOptions} onChange={setStatus} ariaLabel={t("institutionServices.filters.status", "Filter services by status")} />
            <div className="institution-service-filter-surface" ref={filterSurfaceRef}>
              <button ref={filterTriggerRef} type="button" className={`institutions-icon-button institution-clinician-filter-button institution-service-filter-button${advancedFilters.specialty || advancedFilters.facility ? " is-active" : ""}`} aria-label={t("institutionServices.filters.open", "Filter services")} aria-expanded={filterOpen} aria-haspopup="dialog" onClick={() => filterOpen ? closeFilters({ restoreFocus: false }) : openFilters()}><InstitutionIcon src={SERVICE_ICONS.filter} size={20} useMask /></button>
              {filterOpen && !mobileFilterSurface ? <div className="institution-service-filter-popover" role="dialog" aria-modal="false" aria-label={t("institutionServices.filters.title", "Filter services")}><h2 className="text-title-medium text-on-surface">{t("institutionServices.filters.title", "Filter services")}</h2><ServiceFilterFields draft={filterDraft} onChange={setFilterDraft} specialties={specialties} facilities={facilities} t={t} /><div className="institution-service-filter-actions"><button type="button" className="institutions-side-sheet-cancel" onClick={clearFilterDraft}>{t("institutionServices.actions.clear", "Clear")}</button><button type="button" className="institutions-primary-button" onClick={applyFilters}>{t("institutionServices.actions.apply", "Apply")}</button></div></div> : null}
            </div>
          </div>
          {canManage ? <InstitutionRegistryCtas onImport={() => setCsvOpen(true)} onCreate={() => navigate(servicePath(institutionId, "new"))} createLabel={t("institutionRegistryCtas.addService", "Add service")} /> : null}
        </div>
        {rows.length ? (
          <div className="institution-services-table" role="table" aria-label={t("institutionServices.catalogue", "Institution service catalogue")}>
            <div className="institution-service-row institution-service-row--header" role="row">
              <span role="columnheader" className="text-body-medium text-on-surface-variant">{t("institutionServices.columns.service", "Service")}</span>
              <span role="columnheader" className="text-body-medium text-on-surface-variant">{t("institutionServices.columns.specialty", "Specialty")}</span>
              <span role="columnheader" className="text-body-medium text-on-surface-variant">{t("institutionServices.columns.active", "Active")}</span>
              <span role="columnheader" className="text-body-medium text-on-surface-variant">{t("institutionServices.columns.notes", "Notes")}</span>
              <span role="columnheader" className="text-body-medium text-on-surface-variant">{t("institutionServices.columns.price", "Price")}</span>
            </div>
            {rows.map((service) => <ServiceListRow key={service.id} service={service} onOpen={(id) => navigate(servicePath(institutionId, id))} t={t} />)}
          </div>
        ) : <ServicesEmptyState filtered={filtered} t={t} />}
      </section>
      {mobileFilterSurface ? <InstitutionSideSheet open={filterOpen} title={t("institutionServices.filters.title", "Filter services")} onClose={closeFilters} labelledBy="institution-services-filter-title" returnFocusRef={filterTriggerRef} footer={<><button type="button" className="institutions-side-sheet-cancel" onClick={clearFilterDraft}>{t("institutionServices.actions.clear", "Clear")}</button><button type="button" className="institutions-primary-button" onClick={applyFilters}>{t("institutionServices.actions.apply", "Apply")}</button></>}><ServiceFilterFields draft={filterDraft} onChange={setFilterDraft} specialties={specialties} facilities={facilities} t={t} /></InstitutionSideSheet> : null}
      {csvOpen ? <GovernedCsvImport title="Import services from CSV" institutionId={institutionId} importType="service" fields={[{ key: "name", label: "Name", required: true, aliases: ["service"] }, { key: "specialtyId", label: "Specialty ID", required: true, aliases: ["specialty"] }, { key: "price", label: "Price", required: true }, { key: "currency", label: "Currency", required: true }, { key: "facility", label: "Facility", required: true, aliases: ["facility name"] }, { key: "status", label: "Status" }, { key: "notes", label: "Notes" }]} onClose={() => setCsvOpen(false)} /> : null}
      <Snackbar open={Boolean(notice)} message={notice} onClose={() => setNotice("")} />
    </PageShell>
  );
}

export function ServiceEditor({ workspace, serviceId = "", onRefresh }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const institutionId = workspace?.activeInstitution?.id || "";
  const isNew = serviceId === "new";
  const canManage = Boolean(workspace?.canManageServices);
  const createdRecord = location.state?.serviceRecord || null;
  const [record, setRecord] = useState(createdRecord);
  const [loading, setLoading] = useState(!isNew && !createdRecord);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(location.state?.serviceCreated ? t("institutionServices.created", "Service Created") : "");
  const [busy, setBusy] = useState(false);
  const [facilityOpen, setFacilityOpen] = useState(false);
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [deliveryBusy, setDeliveryBusy] = useState(false);
  const [deliveryError, setDeliveryError] = useState(null);
  const [deliverySnapshot, setDeliverySnapshot] = useState(null);
  const [deliveryForm, setDeliveryForm] = useState(deliveryDraft);
  const [deliveryDatePicker, setDeliveryDatePicker] = useState("");
  const [deliveryDateError, setDeliveryDateError] = useState("");
  const [pendingExit, setPendingExit] = useState(null);
  const [form, setForm] = useState(() => serviceFormFromRecord(createdRecord));
  const initialFormRef = useRef(JSON.stringify(serviceFormFromRecord(createdRecord)));
  const overflowRef = useRef(null);
  const overflowTriggerRef = useRef(null);
  const deliveryDateAnchorRefs = useRef({ from: null, to: null });
  const deliveryDateDialogRefs = useRef({ from: null, to: null });
  const deliveryDateTriggerRefs = useRef({ from: null, to: null });
  const isDirty = JSON.stringify(form) !== initialFormRef.current;
  const facilities = Array.isArray(workspace?.serviceFacilities) ? workspace.serviceFacilities : [];
  const specialties = Array.isArray(workspace?.serviceSpecialties) ? workspace.serviceSpecialties : [];
  const c = (key, fallback, values) => t(`institutionServices.${key}`, fallback, values);
  const closeDeliveryDatePicker = useCallback((focusKey = "") => {
    setDeliveryDatePicker("");
    if (focusKey) {
      window.requestAnimationFrame(() => deliveryDateTriggerRefs.current[focusKey]?.focus?.());
    }
  }, []);

  useEffect(() => {
    if (isNew || !institutionId) return undefined;
    let active = true;
    if (!createdRecord) setLoading(true);
    setError("");
    loadInstitutionServiceSnapshot({ institutionId, serviceId })
      .then((snapshot) => {
        if (!active) return;
        const loaded = (snapshot?.services || [])[0] || null;
        setRecord(loaded);
        if (loaded) {
          const nextForm = serviceFormFromRecord(loaded);
          setForm(nextForm);
          initialFormRef.current = JSON.stringify(nextForm);
        }
      })
      .catch((err) => { if (active) setError(err?.message || c("loadError", "Services could not be loaded.")); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [createdRecord, institutionId, isNew, serviceId]);

  const refreshDeliverySnapshot = useCallback(async () => {
    if (isNew || !institutionId || !record?.id) return;
    setDeliveryLoading(true);
    setDeliveryError(null);
    try {
      const snapshot = await loadInstitutionServiceDeliverySnapshot({ institutionId, serviceId: record.id });
      setDeliverySnapshot(snapshot);
    } catch (err) {
      const failure = deliverySnapshotFailure(err);
      console.warn("[Institution service delivery]", {
        operation: failure.operation,
        code: failure.code || "unknown",
        message: failure.kind === "unavailable"
          ? "Delivery assignments are not available in this environment."
          : "Delivery assignment snapshot failed.",
      });
      setDeliveryError(failure);
    } finally {
      setDeliveryLoading(false);
    }
  }, [institutionId, isNew, record?.id]);

  useEffect(() => {
    refreshDeliverySnapshot();
  }, [refreshDeliverySnapshot]);

  useEffect(() => {
    if (!location.state?.serviceCreated) return;
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.pathname, location.search, location.state?.serviceCreated, navigate]);

  useEffect(() => {
    if (!deliveryDatePicker) return undefined;
    const activePicker = deliveryDatePicker;
    const onPointerDown = (event) => {
      const activeAnchor = deliveryDateAnchorRefs.current[activePicker];
      const activeDialog = deliveryDateDialogRefs.current[activePicker];
      const eventPath = event.composedPath?.() || [];
      const pickerSurface = event.target instanceof Element
        ? event.target.closest(".institution-service-delivery-date-picker")
        : null;
      const isInsideDateControl = Object.values(deliveryDateAnchorRefs.current)
        .some((anchor) => anchor?.contains(event.target));
      const isInsideActivePicker = eventPath.includes(activeAnchor)
        || eventPath.includes(activeDialog)
        || pickerSurface === activeDialog
        || activeAnchor?.contains(event.target)
        || activeDialog?.contains(event.target)
        || isInsideDateControl;
      if (!isInsideActivePicker) {
        closeDeliveryDatePicker();
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        closeDeliveryDatePicker(activePicker);
      }
    };
    document.addEventListener("click", onPointerDown);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("click", onPointerDown);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [closeDeliveryDatePicker, deliveryDatePicker]);

  useEffect(() => {
    if (!overflowOpen) return undefined;
    const close = (restoreFocus = false) => {
      setOverflowOpen(false);
      if (restoreFocus) window.requestAnimationFrame(() => overflowTriggerRef.current?.focus?.());
    };
    const onPointerDown = (event) => {
      if (!overflowRef.current?.contains(event.target)) close(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close(true);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [overflowOpen]);

  useEffect(() => {
    if (facilityOpen || currencyOpen || archiveOpen || deliveryOpen) setOverflowOpen(false);
  }, [archiveOpen, currencyOpen, deliveryOpen, facilityOpen]);

  useEffect(() => {
    if (!isDirty) return undefined;
    const onBeforeUnload = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const updatePrice = (value) => update("price", value.replace(/[^0-9.,]/g, ""));
  const updateNotes = (value) => update("notes", normalizeVitalRepBulletLines(value));
  const handleNotesKeyDown = (event) => {
    const edit = getVitalRepBulletKeyEdit({
      key: event.key,
      value: form.notes,
      selectionStart: event.currentTarget.selectionStart,
      selectionEnd: event.currentTarget.selectionEnd,
    });
    if (!edit) return;
    event.preventDefault();
    const textarea = event.currentTarget;
    update("notes", edit.value);
    requestAnimationFrame(() => textarea.setSelectionRange(edit.selectionStart, edit.selectionEnd));
  };
  const leaveToServices = useCallback(() => navigate(servicePath(institutionId)), [institutionId, navigate]);
  const resetDetailDraft = useCallback(() => {
    const nextForm = serviceFormFromRecord(record);
    setForm(nextForm);
    initialFormRef.current = JSON.stringify(nextForm);
    setFacilityOpen(false);
    setCurrencyOpen(false);
  }, [record]);
  const requestExit = useCallback((nextAction) => {
    if (!isDirty) {
      nextAction();
      return;
    }
    setPendingExit(() => nextAction);
    setDiscardOpen(true);
  }, [isDirty]);
  const requestLeave = useCallback(() => requestExit(leaveToServices), [leaveToServices, requestExit]);
  const requestDetailCancel = useCallback(() => requestExit(resetDetailDraft), [requestExit, resetDetailDraft]);
  const cancelDiscard = () => {
    setDiscardOpen(false);
    setPendingExit(null);
  };
  const discard = () => {
    const nextAction = pendingExit;
    setDiscardOpen(false);
    setPendingExit(null);
    nextAction?.();
  };
  const valid = form.name.trim().length >= 2 && form.specialtyId && isValidServicePrice(form.price) && /^[A-Z]{3}$/.test(form.currency) && ["active", "inactive"].includes(form.status) && form.facilityIds.length > 0;
  const submit = async (event) => {
    event.preventDefault();
    if (!valid || busy) return;
    setBusy(true); setError("");
    try {
      const service = { ...form, name: form.name.trim(), notes: form.notes.trim(), price: Number(normalizeServicePriceValue(form.price)) };
      if (!isNew && record?.id) {
        await updateInstitutionService({ institutionId, serviceId: record.id, service });
        const snapshot = await loadInstitutionServiceSnapshot({ institutionId, serviceId: record.id });
        const saved = (snapshot?.services || [])[0] || null;
        if (saved) {
          setRecord(saved);
          const nextForm = serviceFormFromRecord(saved);
          setForm(nextForm);
          initialFormRef.current = JSON.stringify(nextForm);
        }
        setNotice(c("updated", "Service Updated"));
        onRefresh?.();
      } else {
        const result = await createInstitutionService({ institutionId, service });
        const snapshot = await loadInstitutionServiceSnapshot({ institutionId, serviceId: result.id });
        const saved = (snapshot?.services || [])[0] || null;
        initialFormRef.current = JSON.stringify(form);
        onRefresh?.();
        navigate(servicePath(institutionId, result.id), { state: { serviceCreated: true, serviceRecord: saved }, replace: true });
      }
    } catch (err) { setError(err?.message || c("createError", "The service could not be created.")); } finally { setBusy(false); }
  };
  const archive = async () => {
    if (!record?.id || busy) return;
    setBusy(true); setError("");
    try { await archiveInstitutionService({ institutionId, serviceId: record.id, reason: "Archived from Institution Services" }); onRefresh?.(); navigate(servicePath(institutionId), { state: { serviceArchived: true }, replace: true }); } catch (err) { setError(err?.message || c("archiveError", "The service could not be archived.")); setArchiveOpen(false); } finally { setBusy(false); }
  };
  const canonicalDetailUrl = () => {
    if (typeof window === "undefined" || !record?.id) return "";
    return new URL(servicePath(institutionId, record.id), window.location.origin).toString();
  };
  const copyDetailLink = async () => {
    const url = canonicalDetailUrl();
    try {
      if (!url || !navigator.clipboard?.writeText) throw new Error("Clipboard is unavailable");
      await navigator.clipboard.writeText(url);
      setNotice(c("linkCopied", "Institution service link copied."));
    } catch {
      setNotice(c("linkCopyFallback", "Copy the Institution service URL from your browser address bar."));
    }
    setOverflowOpen(false);
  };
  const openDetailInNewTab = () => {
    const url = canonicalDetailUrl();
    if (url) window.open(url, "_blank", "noopener,noreferrer");
    setOverflowOpen(false);
  };
  const removeFacility = (facilityId) => setForm((current) => ({ ...current, facilityIds: current.facilityIds.filter((id) => id !== facilityId) }));
  const assignmentFacilities = form.facilityIds
    .map((id) => facilities.find((facility) => facility.id === id) || (record?.facilities || []).find((facility) => facility.id === id))
    .filter(Boolean);
  const deliveryFacilities = Array.isArray(deliverySnapshot?.facilities) ? deliverySnapshot.facilities : [];
  const eligibleClinicians = Array.isArray(deliverySnapshot?.eligibleClinicians) ? deliverySnapshot.eligibleClinicians : [];
  const deliveryAssignments = Array.isArray(deliverySnapshot?.assignments) ? deliverySnapshot.assignments : [];
  const deliveryCliniciansForFacility = eligibleClinicians.filter((clinician) => (
    Array.isArray(clinician?.facilityIds) && clinician.facilityIds.includes(deliveryForm.facilityId)
      && !(Array.isArray(clinician?.ineligibilityReasons) && clinician.ineligibilityReasons.length)
  ));
  const deliveryDatesValid = !deliveryForm.effectiveTo || !deliveryForm.effectiveFrom || deliveryForm.effectiveTo >= deliveryForm.effectiveFrom;
  const deliveryValid = Boolean(deliveryForm.facilityId && deliveryForm.clinicianId && ["active", "inactive"].includes(deliveryForm.status) && deliveryDatesValid);
  const assignmentGroups = deliveryAssignments.reduce((groups, assignment) => {
    const key = assignment?.facility?.id || "unknown";
    const current = groups.get(key) || { facility: assignment?.facility, assignments: [] };
    current.assignments.push(assignment);
    groups.set(key, current);
    return groups;
  }, new Map());
  const openDeliveryAssignment = () => {
    setDeliveryForm(deliveryDraft());
    setDeliveryDateError("");
    setDeliveryDatePicker("");
    setDeliveryOpen(true);
  };
  const applyDeliveryDate = (field, date) => {
    const nextValue = deliveryDateToValue(date);
    if (!nextValue) return;
    const otherValue = field === "effectiveFrom" ? deliveryForm.effectiveTo : deliveryForm.effectiveFrom;
    if (otherValue && (field === "effectiveFrom" ? nextValue > otherValue : nextValue < otherValue)) {
      setDeliveryDateError(c("delivery.effectiveDateRange", "Effective to cannot be earlier than Effective from."));
      return;
    }
    setDeliveryForm((current) => ({ ...current, [field]: nextValue }));
    setDeliveryDateError("");
    // Let the controlled field receive the selected value before unmounting
    // the calendar and returning focus to its trigger.
    window.requestAnimationFrame(() => {
      closeDeliveryDatePicker(field === "effectiveFrom" ? "from" : "to");
    });
  };
  const createDeliveryAssignment = async () => {
    if (!record?.id || !deliveryValid || deliveryBusy) return;
    setDeliveryBusy(true);
    setDeliveryError(null);
    try {
      await createInstitutionServiceDeliveryAssignment({
        institutionId,
        serviceId: record.id,
        assignment: { ...deliveryForm, sourceType: "manual" },
      });
      await refreshDeliverySnapshot();
      setDeliveryOpen(false);
      setNotice(c("delivery.assigned", "Clinician assigned"));
    } catch (err) {
      console.warn("[Institution service delivery]", { operation: "delivery_create", code: String(err?.code || "unknown"), message: "Delivery assignment creation failed." });
      setDeliveryError({ kind: "error", operation: "delivery_create", code: String(err?.code || "") });
    } finally {
      setDeliveryBusy(false);
    }
  };

  if (!isNew && loading) return <PageShell workspace={workspace} title={c("title", "Services")} icon={ClipboardList} iconSrc={SERVICE_ICONS.page}><RouteFallback label={c("loading", "Loading Services…")} /></PageShell>;
  if (!isNew && error) return <PageShell workspace={workspace} title={c("title", "Services")} icon={ClipboardList} iconSrc={SERVICE_ICONS.page}><div className="medical-institution-error" role="alert"><p>{error}</p><Button type="button" variant="secondary" onClick={() => window.location.reload()}>{c("retry", "Try again")}</Button></div></PageShell>;
  if (!isNew && !record) return <PageShell workspace={workspace} title={c("title", "Services")} icon={ClipboardList} iconSrc={SERVICE_ICONS.page}><div className="medical-institution-error" role="alert"><p>{c("missing", "This service is unavailable in the active parent Institution.")}</p></div><MedicalInstitutionBackButton label={c("backToServices", "Back to Services")} onClick={() => navigate(servicePath(institutionId))} /></PageShell>;
  if (!isNew && record?.status === "archived") return <PageShell workspace={workspace} title={c("title", "Services")} icon={ClipboardList} iconSrc={SERVICE_ICONS.page}><div className="medical-institution-error" role="status"><p>{c("archived", "This service is archived and is no longer in the active catalogue.")}</p></div><MedicalInstitutionBackButton label={c("backToServices", "Back to Services")} onClick={() => navigate(servicePath(institutionId))} /></PageShell>;
  if (!isNew) return (
    <PageShell workspace={workspace} title={c("title", "Services")} icon={ClipboardList} iconSrc={SERVICE_ICONS.page} contentClassName="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
      <section className="institution-service-editor">
        <div className="medical-institution-create-header">
          <div className="medical-institution-title-row"><MedicalInstitutionBackButton label={c("backToServices", "Back to Services")} onClick={() => requestExit(leaveToServices)} /><h1 className="text-headline-small text-on-surface">{record.name}</h1></div>
          {isDirty ? <div className="medical-institution-create-actions"><Button type="button" variant="ghost" onClick={requestDetailCancel}>{c("actions.cancel", "Cancel")}</Button><Button type="button" variant="default" loading={busy} disabled={!valid} onClick={() => submit({ preventDefault() {} })}>{c("actions.save", "Save")}</Button></div> : canManage ? <div className="institution-clinician-overflow-wrap" ref={overflowRef}><button ref={overflowTriggerRef} type="button" className="institutions-icon-button institution-clinician-more-button" aria-label={c("moreActions", "More service actions")} aria-expanded={overflowOpen} aria-haspopup="menu" onClick={() => setOverflowOpen((current) => !current)}><img src={moreVertIcon} alt="" /></button>{overflowOpen ? <div className="institution-clinician-overflow-menu" role="menu" aria-label={c("moreActions", "Service actions")}><button type="button" role="menuitem" onClick={copyDetailLink}><InstitutionIcon src={SERVICE_ICONS.link} size={20} />{c("actions.copyLink", "Copy link")}</button><button type="button" role="menuitem" onClick={openDetailInNewTab}><InstitutionIcon src={SERVICE_ICONS.newTab} size={20} />{c("actions.openInNewTab", "Open in new tab")}</button><span role="separator" /><button type="button" role="menuitem" className="is-destructive" onClick={() => { setArchiveOpen(true); setOverflowOpen(false); }}><InstitutionIcon src={SERVICE_ICONS.delete} size={20} />{c("actions.delete", "Delete")}</button></div> : null}</div> : null}
        </div>
        {error ? <div className="medical-institution-error medical-institution-error--compact" role="alert"><p>{error}</p></div> : null}
        <div className="institution-service-editor-body">
          <section className="medical-institution-form-section">
            <div className="institution-service-detail-basic-information">
              <h2 className="text-body-large text-on-surface-variant">{c("basicInformation", "Basic Information")}</h2>
              <div className="medical-institution-form-grid institution-service-basic-grid">
                <TextField56 label={c("fields.serviceName", "Service Name")} value={record.name} readOnly tone="surface" />
                <TextField56 label={c("fields.specialty", "Specialty")} value={record?.specialty?.name || ""} readOnly tone="surface" />
                <TextField56 className={`institution-service-price-tf56${currencyOpen ? " is-menu-open" : ""}`} label={c("fields.price", "Price")} value={form.price} onChange={updatePrice} inputMode="decimal" tone="surface" clearable clearLabel={c("clearPrice", "Clear price")} leadingSlot={<CurrencyCodeDropdown value={form.currency} onChange={(value) => update("currency", value)} onOpenChange={setCurrencyOpen} alignWithField chevronSrc={SERVICE_ICONS.expandMore} disabled={!canManage || busy} />} inputProps={{ type: "text", autoComplete: "off", readOnly: !canManage }} />
                <ClinicianPickerField className="institution-service-picker-field" label={c("fields.status", "Status")} value={form.status} placeholder="" options={[{ value: "active", label: serviceStatusLabel(t, "active") }, { value: "inactive", label: serviceStatusLabel(t, "inactive") }]} onChange={(value) => update("status", value)} disabled={!canManage || busy} labelLeadingIcon={SERVICE_ICONS.statusPending} valueLeadingIcon={serviceStatusValueIcon(form.status)} trailingIcon={SERVICE_ICONS.expandMore} menuClassName="institution-service-picker-menu" optionClassName="text-body-large text-on-surface" />
              </div>
            </div>
            <div className="institution-service-detail-stack">
              <TextField56 className="institution-service-readonly-notes" as="textarea" prominent textareaResize="vertical" textareaAutoGrow={canManage} label={c("fields.notes", "Indications & Notes")} value={canManage ? form.notes : record.notes || ""} placeholder={c("notSupplied", "Not supplied")} onChange={canManage ? updateNotes : undefined} readOnly={!canManage} disabled={busy} inputProps={canManage ? { onKeyDown: handleNotesKeyDown } : {}} tone="surface" readOnlyValueStyle={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }} />
              <div className="institution-service-assignment-detail">
                <span>{c("fields.assignedFacilities", "Assigned facilities")}</span>
                {assignmentFacilities.length ? <div className="institution-service-chip-row">{assignmentFacilities.map((facility) => <FocusChip key={facility.id} label={facility.name} removable={canManage} onRemove={() => removeFacility(facility.id)} />)}</div> : null}
                {canManage && assignmentFacilities.length === 0 ? <TextField56 className="institution-service-assignment-trigger" as="button" value="" placeholder={c("selectFacilities", "Select Medical Institutions")} onTriggerClick={() => setFacilityOpen(true)} triggerAriaHaspopup="dialog" triggerAriaExpanded={facilityOpen} triggerTrailingInteractive tone="surface" trailingSlot={<InstitutionIcon src={SERVICE_ICONS.expandMore} size={18} />} /> : null}
              </div>
              <section className="institution-service-delivery-detail" aria-labelledby="institution-service-assigned-clinicians">
                <div className="institution-service-delivery-header">
                  <div>
                    <h2 id="institution-service-assigned-clinicians" className="text-title-medium text-on-surface">{c("delivery.title", "Assigned clinicians")}</h2>
                    <p className="text-body-medium text-on-surface-variant">{c("delivery.description", "Clinicians are authorised to deliver this Institution service at the assigned facility.")}</p>
                  </div>
                  {canManage ? <Button type="button" variant="default" size="default" onClick={openDeliveryAssignment}>{c("delivery.assign", "Assign clinician")}</Button> : null}
                </div>
                {deliveryLoading ? <p className="text-body-medium text-on-surface-variant" role="status">{c("delivery.loading", "Loading clinician delivery assignments…")}</p> : null}
                {deliveryError && !deliveryOpen ? <DeliveryAvailabilityState error={deliveryError} onRetry={refreshDeliverySnapshot} c={c} /> : null}
                {!deliveryLoading && !deliveryError && deliveryAssignments.length === 0 ? <div className="institution-service-delivery-empty" role="status"><p className="text-label-medium text-on-surface">{c("delivery.emptyTitle", "No clinicians assigned")}</p><p className="text-body-medium text-on-surface-variant">{deliverySnapshot?.deliveryState === "inactive_service" ? c("delivery.inactiveService", "This service is inactive and cannot be operationally deliverable.") : c("delivery.emptyBody", "Assign an eligible Institution clinician to deliver this service at an authorised facility.")}</p></div> : null}
                {!deliveryLoading && !deliveryError && assignmentGroups.size ? <div className="institution-service-delivery-groups">{Array.from(assignmentGroups.values()).map((group) => <section key={group.facility?.id || "unknown"} className="institution-service-delivery-facility"><h3 className="text-body-medium text-on-surface-variant">{group.facility?.name || c("delivery.unknownFacility", "Authorised facility")}</h3>{group.assignments.map((assignment) => <article key={assignment.id} className="institution-service-delivery-row"><div><span className="text-body-medium text-on-surface">{assignment?.clinician?.fullName || c("delivery.unknownClinician", "Linked clinician")}</span><span>{assignment?.clinician?.specialty || c("notSupplied", "Not supplied")}</span>{assignment?.clinician?.credentialEvidenceState && assignment.clinician.credentialEvidenceState !== "verified" ? <small>{c("delivery.credentialWarning", "Credential evidence: {state}", { state: assignment.clinician.credentialEvidenceState.replaceAll("_", " ") })}</small> : null}</div><span className={`institution-service-delivery-state is-${assignment.operationalState}`}>{deliveryStateLabel(t, assignment.operationalState)}</span>{assignment.effectiveFrom ? <small>{c("delivery.effective", "Effective {from}{until}", { from: assignment.effectiveFrom, until: assignment.effectiveTo ? ` – ${assignment.effectiveTo}` : "" })}</small> : null}</article>)}</section>)}</div> : null}
              </section>
            </div>
          </section>
        </div>
      </section>
      <InstitutionSideSheet open={facilityOpen} title={c("fields.assignedFacilities", "Assigned facilities")} description={c("facilityAssignmentSupport", "A service requires one or more active Medical Institutions in this parent workspace.")} onClose={() => setFacilityOpen(false)} labelledBy="institution-service-facility-title" footer={<><button type="button" className="institutions-side-sheet-cancel" onClick={() => setFacilityOpen(false)}>{c("actions.cancel", "Cancel")}</button><button type="button" className="institutions-primary-button" onClick={() => setFacilityOpen(false)}>{c("actions.done", "Done")}</button></>}><div className="institution-clinician-facility-options" role="group" aria-label={c("fields.assignedFacilities", "Assigned facilities")}>{facilities.map((facility) => { const selected = form.facilityIds.includes(facility.id); return <button key={facility.id} type="button" className={selected ? "is-selected" : ""} aria-pressed={selected} onClick={() => update("facilityIds", selected ? form.facilityIds.filter((id) => id !== facility.id) : [...form.facilityIds, facility.id])}><span><strong>{facility.name}</strong><small>{facility.reference ? `ID: ${facility.reference}` : c("activeMedicalInstitution", "Active Medical Institution")}</small></span><span aria-hidden="true">{selected ? c("selected", "Selected") : c("select", "Select")}</span></button>; })}</div></InstitutionSideSheet>
      <InstitutionSideSheet open={deliveryOpen} title={c("delivery.assign", "Assign clinician")} description={c("delivery.assignSupport", "Choose a service facility, then an eligible clinician already linked to that facility.")} onClose={() => { if (!deliveryBusy) { setDeliveryDatePicker(""); setDeliveryOpen(false); } }} pending={deliveryBusy} labelledBy="institution-service-delivery-title" footer={<><Button type="button" variant="tonal" size="default" disabled={deliveryBusy} onClick={() => { setDeliveryDatePicker(""); setDeliveryOpen(false); }}>{c("actions.cancel", "Cancel")}</Button><Button type="button" variant="default" size="default" disabled={!deliveryValid || deliveryBusy || Boolean(deliveryError)} onClick={createDeliveryAssignment}>{c("delivery.assign", "Assign clinician")}</Button></>}>
        <div className={`institutions-side-sheet-form institution-service-delivery-sheet${deliveryDatePicker ? " is-date-picker-open" : ""}`}>
          {deliveryError ? <DeliveryAvailabilityState error={deliveryError} onRetry={refreshDeliverySnapshot} c={c} /> : null}
          <Select label={c("delivery.facility", "Facility")} value={deliveryForm.facilityId} disabled={Boolean(deliveryError)} placeholder={c("selectOption", "Select an option")} options={[{ value: "", label: c("delivery.selectFacility", "Select a facility") }, ...deliveryFacilities.map((facility) => ({ value: facility.id, label: facility.name }))]} onValueChange={(value) => setDeliveryForm((current) => ({ ...current, facilityId: value, clinicianId: "" }))} tone="surface" />
          <Select label={c("delivery.clinician", "Clinician")} value={deliveryForm.clinicianId} disabled={Boolean(deliveryError) || !deliveryForm.facilityId} placeholder={c("selectOption", "Select an option")} options={[{ value: "", label: deliveryForm.facilityId ? c("delivery.selectClinician", "Select an eligible clinician") : c("delivery.selectFacilityFirst", "Choose a facility first") }, ...deliveryCliniciansForFacility.map((clinician) => ({ value: clinician.id, label: `${clinician.fullName}${clinician.specialty ? ` — ${clinician.specialty}` : ""}` }))]} onValueChange={(value) => setDeliveryForm((current) => ({ ...current, clinicianId: value }))} tone="surface" />
          {deliveryForm.facilityId && deliveryCliniciansForFacility.length === 0 ? <p className="text-body-medium text-on-surface-variant" role="status">{c("delivery.noneEligible", "No eligible active Institution clinicians are linked to this facility.")}</p> : null}
          <Select label={c("fields.status", "Status")} value={deliveryForm.status} disabled={Boolean(deliveryError)} options={[{ value: "active", label: serviceStatusLabel(t, "active") }, { value: "inactive", label: serviceStatusLabel(t, "inactive") }]} onValueChange={(value) => setDeliveryForm((current) => ({ ...current, status: value }))} tone="surface" />
          <div ref={(node) => { deliveryDateAnchorRefs.current.from = node; }} className={`institution-service-delivery-date-anchor institution-service-delivery-date-anchor--from${deliveryDatePicker === "from" ? " is-open" : ""}`}><TextField56 label={c("delivery.effectiveFrom", "Effective from")} as="button" value={formatDeliveryDate(deliveryForm.effectiveFrom)} placeholder={c("delivery.selectDate", "Select date")} onTriggerClick={() => { setDeliveryDateError(""); setDeliveryDatePicker((activePicker) => activePicker === "from" ? "" : "from"); }} triggerAriaLabel={c("delivery.effectiveFrom", "Effective from")} triggerAriaHaspopup="dialog" triggerAriaExpanded={deliveryDatePicker === "from"} triggerProps={{ ref: (node) => { deliveryDateTriggerRefs.current.from = node; } }} tone="surface" leadingIcon={SERVICE_ICONS.dateRange} />{deliveryDatePicker === "from" ? <div ref={(node) => { deliveryDateDialogRefs.current.from = node; }} className="institution-service-delivery-date-picker" role="dialog" aria-label={c("delivery.effectiveFrom", "Effective from")} onPointerDown={(event) => event.stopPropagation()} onClick={(event) => event.stopPropagation()}><DatePicker value={deliveryDateFromValue(deliveryForm.effectiveFrom)} maxDate={deliveryDateFromValue(deliveryForm.effectiveTo) || undefined} commitOnSelect onChange={(date) => applyDeliveryDate("effectiveFrom", date)} onConfirm={(date) => applyDeliveryDate("effectiveFrom", date)} onCancel={() => closeDeliveryDatePicker("from")} /></div> : null}</div>
          <div ref={(node) => { deliveryDateAnchorRefs.current.to = node; }} className={`institution-service-delivery-date-anchor${deliveryDatePicker === "to" ? " is-open" : ""}`}><TextField56 label={c("delivery.effectiveTo", "Effective to (optional)")} as="button" value={formatDeliveryDate(deliveryForm.effectiveTo)} placeholder={c("delivery.selectDate", "Select date")} onTriggerClick={() => { setDeliveryDateError(""); setDeliveryDatePicker((activePicker) => activePicker === "to" ? "" : "to"); }} triggerAriaLabel={c("delivery.effectiveTo", "Effective to (optional)")} triggerAriaHaspopup="dialog" triggerAriaExpanded={deliveryDatePicker === "to"} triggerProps={{ ref: (node) => { deliveryDateTriggerRefs.current.to = node; } }} tone="surface" leadingIcon={SERVICE_ICONS.dateRange} />{deliveryDatePicker === "to" ? <div ref={(node) => { deliveryDateDialogRefs.current.to = node; }} className="institution-service-delivery-date-picker" role="dialog" aria-label={c("delivery.effectiveTo", "Effective to (optional)")} onPointerDown={(event) => event.stopPropagation()} onClick={(event) => event.stopPropagation()}><DatePicker value={deliveryDateFromValue(deliveryForm.effectiveTo)} minDate={deliveryDateFromValue(deliveryForm.effectiveFrom) || undefined} commitOnSelect onChange={(date) => applyDeliveryDate("effectiveTo", date)} onConfirm={(date) => applyDeliveryDate("effectiveTo", date)} onCancel={() => closeDeliveryDatePicker("to")} /></div> : null}</div>
          {deliveryDateError ? <p className="medical-institution-sheet-error" role="alert">{deliveryDateError}</p> : null}
        </div>
      </InstitutionSideSheet>
      <BasicDialog open={discardOpen} title={c("discardTitle", "Discard changes?")} body={c("discardBody", "Are you sure you want to discard changes made? This action cannot be undone.")} cancelText={c("actions.cancel", "Cancel")} okText={c("actions.discard", "Discard")} onCancel={cancelDiscard} onOk={discard} disableBackdropClose={busy} actionClassName="text-label-large text-primary" actionTextColor="rgb(var(--afrimed-primary))" />
      <BasicDialogCritical open={archiveOpen} title={c("archiveTitle", `Delete “${record.name}” Service?`, { name: record.name })} body={c("archiveBody", "This service will be deleted from your Medical Institution. This action cannot be undone.")} cancelText={c("actions.cancel", "Cancel")} okText={c("actions.delete", "Delete")} onCancel={() => setArchiveOpen(false)} onOk={archive} disableBackdropClose={busy} />
      <Snackbar open={Boolean(notice)} message={notice} onClose={() => setNotice("")} />
    </PageShell>
  );
  if (!canManage) return <PageShell workspace={workspace} title={c("new", "New Service")} icon={ClipboardList} iconSrc={SERVICE_ICONS.page}><div className="medical-institution-error" role="alert"><p>{c("writeDenied", "You do not have permission to manage this parent service catalogue.")}</p></div><MedicalInstitutionBackButton label={c("backToServices", "Back to Services")} onClick={() => navigate(servicePath(institutionId))} /></PageShell>;
  return (
    <PageShell workspace={workspace} title={c("title", "Services")} icon={ClipboardList} iconSrc={SERVICE_ICONS.page} contentClassName="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
      <form className="institution-service-editor" onSubmit={submit}>
        <div className="medical-institution-create-header">
          <div className="medical-institution-title-row">
            <MedicalInstitutionBackButton label={c("backToServices", "Back to Services")} onClick={requestLeave} />
            <h1 className="text-headline-small text-on-surface">{c("new", "New Service")}</h1>
          </div>
          <div className="medical-institution-create-actions">
            <Button type="button" variant="ghost" onClick={requestLeave}>{c("actions.cancel", "Cancel")}</Button>
            <Button type="submit" variant="default" loading={busy} disabled={!valid}>{c("actions.save", "Save")}</Button>
          </div>
        </div>

        {error ? <div className="medical-institution-error medical-institution-error--compact" role="alert"><p>{error}</p></div> : null}

        <div className="institution-service-editor-body">
          <section className="medical-institution-form-section">
            <h2 className="text-body-large text-on-surface-variant">{c("basicInformation", "Basic Information")}</h2>
            <div className="medical-institution-form-grid institution-service-basic-grid">
              <TextField56 label={`${c("fields.serviceName", "Service Name")}*`} value={form.name} onChange={(value) => update("name", value)} tone="surface" clearable clearLabel={c("actions.clear", "Clear")} />
              <ClinicianPickerField className="institution-service-picker-field" label={`${c("fields.specialty", "Specialty")}*`} value={form.specialtyId} placeholder="" options={specialties.map((specialty) => ({ value: specialty.id, label: specialty.name }))} onChange={(value) => update("specialtyId", value)} trailingIcon={SERVICE_ICONS.expandMore} menuClassName="institution-service-picker-menu" optionClassName="text-body-large text-on-surface" emptyLabelAsPlaceholder />
              <TextField56 className={`institution-service-price-tf56${currencyOpen ? " is-menu-open" : ""}`} label={`${c("fields.price", "Price")}*`} value={form.price} onChange={updatePrice} inputMode="decimal" tone="surface" clearable clearLabel={c("clearPrice", "Clear price")} leadingSlot={<CurrencyCodeDropdown value={form.currency} onChange={(value) => update("currency", value)} onOpenChange={setCurrencyOpen} alignWithField chevronSrc={SERVICE_ICONS.expandMore} disabled={busy} />} inputProps={{ type: "text", autoComplete: "off" }} />
              <ClinicianPickerField className="institution-service-picker-field" label={`${c("fields.status", "Status")}*`} value={form.status} placeholder="" options={[{ value: "active", label: serviceStatusLabel(t, "active") }, { value: "inactive", label: serviceStatusLabel(t, "inactive") }]} onChange={(value) => update("status", value)} labelLeadingIcon={SERVICE_ICONS.statusPending} valueLeadingIcon={serviceStatusValueIcon(form.status)} trailingIcon={SERVICE_ICONS.expandMore} menuClassName="institution-service-picker-menu" optionClassName="text-body-large text-on-surface" />
            </div>
            <TextField56 as="textarea" prominent textareaResize="vertical" label={c("fields.notes", "Indications & Notes")} value={form.notes} onChange={updateNotes} tone="surface" inputProps={{ rows: 5, onKeyDown: handleNotesKeyDown }} />
            <TextField56 as="button" label={`${c("fields.assignedFacilities", "Assigned facilities")}*`} value={form.facilityIds.length ? c("facilityCount", "{count} selected", { count: form.facilityIds.length }) : ""} placeholder={c("selectFacilities", "Select Medical Institutions")} onTriggerClick={() => setFacilityOpen(true)} triggerAriaHaspopup="dialog" triggerAriaExpanded={facilityOpen} triggerTrailingInteractive tone="surface" trailingSlot={<img src="/icons/appointments/dashboard/Show_less.svg" alt="" className="h-5 w-5 -rotate-90" />} />
          </section>
        </div>
      </form>

      <InstitutionSideSheet open={facilityOpen} title={c("fields.assignedFacilities", "Assigned facilities")} description={c("facilityAssignmentSupport", "A service requires one or more active Medical Institutions in this parent workspace.")} onClose={() => setFacilityOpen(false)} labelledBy="institution-service-facility-title" footer={<><button type="button" className="institutions-side-sheet-cancel" onClick={() => setFacilityOpen(false)}>{c("actions.cancel", "Cancel")}</button><button type="button" className="institutions-primary-button" onClick={() => setFacilityOpen(false)}>{c("actions.done", "Done")}</button></>}>
        <div className="institution-clinician-facility-options" role="group" aria-label={c("fields.assignedFacilities", "Assigned facilities")}>
          {facilities.map((facility) => {
            const selected = form.facilityIds.includes(facility.id);
            return <button key={facility.id} type="button" className={selected ? "is-selected" : ""} aria-pressed={selected} onClick={() => update("facilityIds", selected ? form.facilityIds.filter((id) => id !== facility.id) : [...form.facilityIds, facility.id])}><span><strong>{facility.name}</strong><small>{facility.reference ? `ID: ${facility.reference}` : c("activeMedicalInstitution", "Active Medical Institution")}</small></span><span aria-hidden="true">{selected ? c("selected", "Selected") : c("select", "Select")}</span></button>;
          })}
        </div>
      </InstitutionSideSheet>
      <BasicDialog open={discardOpen} title={c("discardTitle", "Discard changes?")} body={c("discardBody", "Are you sure you want to discard changes made? This action cannot be undone.")} cancelText={c("actions.cancel", "Cancel")} okText={c("actions.discard", "Discard")} onCancel={cancelDiscard} onOk={discard} disableBackdropClose={busy} actionClassName="text-label-large text-primary" actionTextColor="rgb(var(--afrimed-primary))" />
    </PageShell>
  );
}
